// Vanilla ScoutIQ Course View (Non-React)
// ------------------------------------------------------------
// Provides a template + tab logic that visually mimics the React CourseView
// minus the Chat tab. Injected only inside #scoutiq-course-view.
// Relies exclusively on data-* attributes present on .scoutiq-course-card elements.

import { EndeavorApi } from '../../services/api/EndeavorApi';

export function createScoutIQCourseViewHTML(course) {
  const safe = (v) => (v == null ? '' : String(v));
  // Extract metadata for header chips
  const getMeta = (name) => {
    try {
      const raws = course?.metadata || course?.metadataItems || course?.metadataValues || [];
      for (const it of raws) {
        const n = (it?.metadataDefinition?.name || it?.metadataName || it?.name || '').toLowerCase();
        if (n.includes(name)) return it?.value || it?.val || it?.text || '';
      }
    } catch(e){}
    return '';
  };
  const professor = safe(course.professor) || getMeta('professor');
  const location = safe(course.location) || safe(course.room) || getMeta('room') || getMeta('location');
  const courseCode = safe(course.courseCode) || safe(course.code) || getMeta('course code');
  return `
    <div class="course-view-wrapper">
      <div class="course-view-header" role="group" aria-label="Course header">
        <div class="course-header-row">
          <button id="scoutiq-back-btn" class="back-icon-btn" aria-label="Back to dashboard" type="button">
            <svg viewBox="0 0 20 20" class="back-icon-svg" aria-hidden="true" focusable="false">
              <path d="M12 4l-6 6 6 6" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
          </button>
          <div class="course-header-content">
            <h1 class="course-view-title">${safe(course.title)}</h1>
            <div class="course-header-meta" id="course-header-meta">
              ${courseCode ? `<span class="header-meta-chip code-chip" title="Course code">${safe(courseCode)}</span>` : ''}
              ${professor ? `<span class="header-meta-chip prof-chip"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>${safe(professor)}</span>` : ''}
              ${location ? `<span class="header-meta-chip loc-chip"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>${safe(location)}</span>` : ''}
            </div>
          </div>
          <div class="course-header-actions">
            <button type="button" class="header-action-btn header-delete-btn" id="course-header-delete-btn" aria-label="Delete course" title="Delete course">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M3 6h18"/><path d="M8 6V4h8v2"/>
                <path d="M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14"/>
                <path d="M10 11v6"/><path d="M14 11v6"/>
              </svg>
            </button>
            <button type="button" class="header-action-btn course-info-toggle header-info-btn" data-state="collapsed" aria-label="Course info" title="Course info">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="16" x2="12" y2="12"/>
                <line x1="12" y1="8" x2="12.01" y2="8"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div class="course-info-section" data-state="collapsed">
        <div class="course-info-container course-info-card" data-testid="course-info-card">
          <div id="course-info-react-portal" data-portal-ready="false"></div>
        </div>
      </div>
      <div class="course-view-body">
        <div class="course-tabs" role="tablist" aria-label="Course sections">
          <button class="course-tab is-active"
                  data-tab="description"
                  role="tab"
                  aria-selected="true"
                  aria-controls="tab-panel-description"
                  id="tab-description"
                  tabindex="0">Activities</button>
          <button class="course-tab"
                  data-tab="chat"
                  role="tab"
                  aria-selected="false"
                  aria-controls="tab-panel-chat"
                  id="tab-chat"
                  tabindex="-1">Chat</button>
          <button class="course-tab"
                  data-tab="calendar"
                  role="tab"
                  aria-selected="false"
                  aria-controls="tab-panel-calendar"
                  id="tab-calendar"
                  tabindex="-1">Calendar</button>
          <button class="course-tab"
                  data-tab="documents"
                  role="tab"
                  aria-selected="false"
                  aria-controls="tab-panel-documents"
                  id="tab-documents"
                  tabindex="-1">Documents</button>
          <button class="course-tab"
                  data-tab="notes"
                  role="tab"
                  aria-selected="false"
                  aria-controls="tab-panel-notes"
                  id="tab-notes"
                  tabindex="-1">Notes</button>
          <button class="course-tab"
                  data-tab="syllabus"
                  role="tab"
                  aria-selected="false"
                  aria-controls="tab-panel-syllabus"
                  id="tab-syllabus"
                  tabindex="-1">Syllabus</button>
        </div>
        <div class="course-tab-panels-wrapper">
        <div class="course-tab-panels" aria-live="polite">
        <section class="course-tab-panel is-active" data-tab="description" role="tabpanel" id="tab-panel-description" aria-labelledby="tab-description">
          <!-- React UnifiedActivities portal mounts here -->
          <div id="scoutiq-activities-portal" data-portal-ready="false" data-course-id="${course.id}">
            <div class="panel-placeholder">Loading activities…</div>
          </div>
        </section>
        <section class="course-tab-panel" data-tab="chat" role="tabpanel" id="tab-panel-chat" aria-labelledby="tab-chat" tabindex="0">
          <div id="scoutiq-course-chat-container" class="scoutiq-course-chat-panel">
            <div class="panel-placeholder">Loading chat…</div>
          </div>
        </section>
        <section class="course-tab-panel" data-tab="calendar" role="tabpanel" id="tab-panel-calendar" aria-labelledby="tab-calendar" tabindex="0">
          <div class="course-calendar-wrapper">
            <button 
              class="course-calendar-add-btn" 
              data-course-id="${escapeHtml(course.id)}"
              onclick="window.openCourseCalendarAddModal && window.openCourseCalendarAddModal('${escapeHtml(course.id)}')" 
              aria-label="Add event or assignment to this course">
              Add New
            </button>
            <div id="scoutiq-course-calendar-container">
              <div class="panel-placeholder">Calendar view placeholder.</div>
            </div>
          </div>
        </section>
        <section class="course-tab-panel" data-tab="documents" role="tabpanel" id="tab-panel-documents" aria-labelledby="tab-documents" tabindex="0">
          <div id="scoutiq-course-docs-container">
            <div class="panel-placeholder">Documents list placeholder.</div>
          </div>
        </section>
        <section class="course-tab-panel" data-tab="notes" role="tabpanel" id="tab-panel-notes" aria-labelledby="tab-notes" tabindex="0">
          <div id="notes-content">
            <div class="panel-placeholder">Notes list placeholder.</div>
          </div>
        </section>
        <section class="course-tab-panel" data-tab="syllabus" role="tabpanel" id="tab-panel-syllabus" aria-labelledby="tab-syllabus" tabindex="0">
          <div id="scoutiq-course-syllabus-container">
            <div class="panel-placeholder">No syllabus uploaded yet.</div>
          </div>
        </section>
      </div>
      </div>
      </div>
    </div>`;
}

export function initScoutIQCourseTabs(root = document.getElementById('scoutiq-course-view'), initialTabName = null) {
  if (!root) {
    console.log('[ScoutIQ] Course view root not found, skipping tab initialization');
    return;
  }
  const tabs = Array.from(root.querySelectorAll('.course-tab'));
  const panels = Array.from(root.querySelectorAll('.course-tab-panel'));
  if (tabs.length === 0 || panels.length === 0) {
    console.log('[ScoutIQ] No tabs or panels found, skipping tab initialization');
    return;
  }
  function activateTab(tab) {
    const target = tab.dataset.tab;
    tabs.forEach(t => {
      const isActive = t === tab;
      t.classList.toggle('is-active', isActive);
      t.setAttribute('aria-selected', isActive ? 'true' : 'false');
      t.tabIndex = isActive ? 0 : -1;
    });
    panels.forEach(panel => {
      const match = panel.dataset.tab === target;
      panel.classList.toggle('is-active', match);
    });
    
    // Set data-active-tab attribute on root for CSS scoping
    root.setAttribute('data-active-tab', target);
    
    tab.focus();
    
    // Dispatch custom event to notify React of tab change
    try {
      window.dispatchEvent(new CustomEvent('scoutiq:tabChanged', { detail: { tab: target } }));
    } catch (e) {
      console.warn('Failed to dispatch tab change event:', e);
    }
  }
  tabs.forEach(tab => {
    if (tab.dataset.bound === 'true') return;
    tab.dataset.bound = 'true';
    tab.addEventListener('click', () => activateTab(tab));
    tab.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const idx = tabs.indexOf(tab);
        const dir = e.key === 'ArrowRight' ? 1 : -1;
        const next = (idx + dir + tabs.length) % tabs.length;
        activateTab(tabs[next]);
      }
      if (e.key === 'Home') { e.preventDefault(); activateTab(tabs[0]); }
      if (e.key === 'End') { e.preventDefault(); activateTab(tabs[tabs.length - 1]); }
    });
  });
  
  // Restore previously active tab if specified
  if (initialTabName) {
    const tabToActivate = tabs.find(t => t.dataset.tab === initialTabName);
    if (tabToActivate) {
      activateTab(tabToActivate);
    }
  }

  const backBtn = root.querySelector('#scoutiq-back-btn');
  if (backBtn && backBtn.dataset.bound !== 'true') {
    backBtn.dataset.bound = 'true';
    backBtn.addEventListener('click', () => {
      // Only dispatch the clear selection event; React side will handle showing the home view.
      // Removing direct DOM class toggling avoids a brief flicker where both views flash.
      try { window.dispatchEvent(new CustomEvent('scoutiq:clearSelection')); } catch { /* no-op */ }
      // Light UX enhancement: mark existing active cards inactive immediately (cosmetic) without toggling views.
      document.querySelectorAll('.scoutiq-course-card.is-active').forEach(c => c.classList.remove('is-active'));
    });
  }
}

// Extended signature: optionally accept pre-fetched React side arrays of assignments/events.
// This allows us to surface a lightweight grouped "At a Glance" under the description tab
// without forcing React rendering inside the vanilla injected view.
export function showScoutIQCourseView(course, { assignments = [], events = [] } = {}) {
  const mainView = document.getElementById('scoutiq-main-view');
  const courseView = document.getElementById('scoutiq-course-view');
  if (!courseView || !mainView) {
    console.error('[ScoutIQ] Cannot show course view - required DOM elements not found');
    return;
  }
  if (!course || !course.id) {
    console.error('[ScoutIQ] Cannot show course view - invalid course data');
    return;
  }
  
  // Check if we're already showing this course (data update vs initial render)
  const existingCourseId = courseView.getAttribute('data-current-course-id');
  const isDataUpdate = existingCourseId === String(course.id) && !courseView.classList.contains('hidden');
  
  // Preserve the currently active tab before rebuilding HTML
  const activeTab = courseView.querySelector('.course-tab.is-active');
  const activeTabName = activeTab ? activeTab.dataset.tab : null;
  
  markActiveCourseCard(String(course.id));
  mainView.classList.add('hidden');
  courseView.classList.remove('hidden');
  
  // Only rebuild HTML if this is an initial course selection, not a data update
  if (!isDataUpdate) {
    courseView.innerHTML = createScoutIQCourseViewHTML(course);
    courseView.setAttribute('data-current-course-id', String(course.id));
  }

  // Update the left-course card pill with any available course code so sidebar doesn't show N/A
  try {
    const leftCard = getLeftCourseCardEl(String(course.id || ''));
    const displayCode = course?.code || course?.shortName || course?.courseCode || (function(){
      try {
        const raws = course?.metadata || course?.metadataItems || course?.metadataValues || [];
        for (const it of raws) {
          const name = (it?.metadataDefinition?.name || it?.metadataName || it?.name || '').toLowerCase();
          if (name === 'course code' || name === 'code') return it?.value || it?.val || it?.text || '';
        }
      } catch(e){}
      return '';
    })();
    if (leftCard) {
      leftCard.setAttribute('data-course-code', displayCode || '');
      const pill = leftCard.querySelector('.scoutiq-course-pill');
      if (pill) {
        if (displayCode) {
          pill.textContent = displayCode;
          pill.setAttribute('title', displayCode);
          pill.style.display = '';
        } else {
          pill.style.display = 'none';
        }
      }
    }
  } catch (e) { /* ignore */ }

  // Metadata simplified: chips rendered directly in header; no panel grid.

  // Populate events & assignments & calendar before wiring tabs
  try {
    const courseId = String(course.id || '');
  // New integrated columns containers
  const eventsList = courseView.querySelector('#course-events-list');
  const eventsCountsEl = courseView.querySelector('#course-events-counts');
  const eventsTimelineFilter = courseView.querySelector('#events-timeline-filter');
  const assignmentsList = courseView.querySelector('#course-assignments-list');
  const assignmentsCountsEl = courseView.querySelector('#course-assignments-counts');
  const assignmentsFilter = courseView.querySelector('#assignments-filter');
    const calendarContainer = courseView.querySelector('#scoutiq-course-calendar-container');
    const syllabusContainer = courseView.querySelector('#scoutiq-course-syllabus-container');
  // Removed glance summary per latest request (only show Events & Assignments sections)

    const allEventCards = document.querySelectorAll(`.scoutiq-event-card[data-course-id="${CSS.escape(courseId)}"]`);
    const allAssignmentCards = document.querySelectorAll(`.scoutiq-assignment-card[data-course-id="${CSS.escape(courseId)}"]`);

    // Normalize incoming React-provided arrays first; fallback to DOM-derived cards if empty
    const fallbackEvents = Array.from(allEventCards).map(card => ({
      type: 'Event',
      title: card.querySelector('.scoutiq-course-upcoming-card__title')?.textContent || 'Untitled Event',
      dueDate: card.querySelector('.scoutiq-course-upcoming-card__date')?.textContent || '',
      app: card.dataset.app || card.dataset.source || null,
      startDatetime: extractDateAttr(card)
    }));
    const fallbackAssignments = Array.from(allAssignmentCards).map(card => ({
      type: 'Assignment',
      title: card.querySelector('.scoutiq-course-upcoming-card__title')?.textContent || 'Untitled Assignment',
      dueDate: card.querySelector('.scoutiq-course-upcoming-card__date')?.textContent || '',
      app: card.dataset.app || card.dataset.source || null,
      dueDatetime: extractDateAttr(card)
    }));
    // Normalization with broader fallbacks to prevent blank titles/dates.
    // Many upstream integrations use variant field names; include them here.
    const normEvents = (events || []).map(e => ({
      ...e, // Preserve ALL original fields including ID
      type: 'Event',
      title: e.title || e.name || e.eventName || e.assignmentName || e.endeavorName || 'Untitled Event',
      startDatetime: e.startDatetime || e.eventDate || e.startDate || e.dueDate || e.due || e.date || e.datetime || e.timestamp || '',
      app: e.app || e.source || e.platform || e.origin || null,
      endeavorName: e.courseName || e.endeavorName || e.course?.name || null
    }));
    
    // Deduplicate events by ID before using them
    let effectiveEvents = normEvents.length ? normEvents : fallbackEvents;
    const seenEventIds = new Set();
    effectiveEvents = effectiveEvents.filter(e => {
      const id = e.id || e.eventId || e.eventID || e.event_id;
      if (!id) return true; // Keep events without IDs
      if (seenEventIds.has(String(id))) {
        return false;
      }
      seenEventIds.add(String(id));
      return true;
    });
    
    // Store events globally immediately so updates can modify the array
    window.scoutiqEffectiveEvents = effectiveEvents;
    
    const normAssignments = (assignments || []).map(a => ({
      ...a, // Preserve ALL original fields including ID
      type: 'Assignment',
      title: a.title || a.name || a.assignmentName || a.endeavorName || a.taskName || a.label || 'Untitled Assignment',
      // Accept multiple due date variants.
      dueDatetime: a.dueDatetime || a.dueDate || a.dueAt || a.due_at || a.due || a.startTime || a.date || a.timestamp || '',
      app: a.app || a.source || a.platform || a.origin || null,
      completed: a.completed === true || a.completed === 'true' || a.status === 'Completed' || a.submissionStatus === 'Completed',
      endeavorName: a.courseName || a.endeavorName || a.course?.name || null
    }));
    
    // Deduplicate assignments by ID before using them
    let effectiveAssignments = normAssignments.length ? normAssignments : fallbackAssignments;
    const seenIds = new Set();
    effectiveAssignments = effectiveAssignments.filter(a => {
      const id = a.id || a.assignmentId || a.assignmentID || a.assignment_id;
      if (!id) return true; // Keep assignments without IDs (shouldn't happen but safe fallback)
      if (seenIds.has(String(id))) {
        return false;
      }
      seenIds.add(String(id));
      return true;
    });
    
    // REMOVED: Legacy toggle state merge was causing stale data to overwrite fresh server data
    // The assignments from DataContext already have the correct, authoritative completed status
    // If window.scoutiqEffectiveAssignments exists, merge completion status from it
    // COMMENTED OUT - this was overwriting correct data from React/DataContext with stale data
    // if (window.scoutiqEffectiveAssignments && window.scoutiqEffectiveAssignments.length > 0) {
    //   const existingMap = new Map();
    //   window.scoutiqEffectiveAssignments.forEach(a => {
    //     const id = a.id || a.assignmentId || a.assignmentID || a.assignment_id;
    //     if (id) {
    //       existingMap.set(String(id), {
    //         completed: a.completed,
    //         status: a.status
    //       });
    //     }
    //   });
    //   
    //   // Apply existing toggle states to new data
    //   effectiveAssignments = effectiveAssignments.map(a => {
    //     const id = a.id || a.assignmentId || a.assignmentID || a.assignment_id;
    //     if (id && existingMap.has(String(id))) {
    //       const existing = existingMap.get(String(id));
    //       console.log('[ScoutIQ Merge] Preserving toggle state for assignment:', id, existing);
    //       return {
    //         ...a,
    //         completed: existing.completed,
    //         status: existing.status
    //       };
    //     }
    //     return a;
    //   });
    // }
    
    // Store assignments globally immediately so toggle function can modify it
    window.scoutiqEffectiveAssignments = effectiveAssignments;

    // Populate events & assignments & calendar before wiring tabs
    function refreshEvents(){
      if(!eventsList) return;
      const mode = eventsTimelineFilter?.value || 'upcoming';
      // Use global events array to pick up updates
      const currentEvents = window.scoutiqEffectiveEvents || effectiveEvents;
      const filtered = currentEvents.filter(ev => filterEvent(ev, mode));
      
      // Store event objects globally for modal access
      if (!window.scoutiqEventObjects) {
        window.scoutiqEventObjects = new Map();
      }
      
      // Store each event by its ID
      filtered.forEach(event => {
        const id = event.id || event.eventId || event.eventID || event.event_id;
        if (id) {
          window.scoutiqEventObjects.set(String(id), event);
        }
      });
      
  eventsList.innerHTML = filtered.length ? filtered.map(ev => buildActivityCard(ev,'event')).join('') : '<div class="events-empty-state"><img src="/scout_images/scout_vacation.png" alt="No events" class="empty-state-image" /><p class="empty-state-text">Looks like you\'re free! Add an event if you need one.</p></div>';
      
      // Wire event card click handlers after DOM update
      setTimeout(() => {
        wireEventCards(eventsList);
      }, 0);
      
      if(eventsCountsEl){
        const counts = tallyEvents(currentEvents);
        eventsCountsEl.textContent = `${counts.eventPast} past, ${counts.eventUpcoming} upcoming`;
        
        // Update status chips
        const eventsStatusChips = courseView.querySelector('#events-status-chips');
        if (eventsStatusChips) {
          const totalEvents = counts.eventPast + counts.eventUpcoming;
          // TODO: Calculate actual "this week" count - for now using placeholder
          const eventsThisWeek = 0; // Placeholder - add week filter logic later
          eventsStatusChips.innerHTML = `
            <span class="ca-status-chip">This week: ${eventsThisWeek}</span>
            <span class="ca-status-chip">Total: ${totalEvents}</span>
          `;
        }
      }
      // Calculate and update events progress bar
      const totalEvents = currentEvents.length;
      const pastEvents = currentEvents.filter(ev => {
        const raw = ev.startDatetime;
        if (!raw) return false;
        const dt = new Date(raw);
        return !isNaN(dt) && dt.getTime() < Date.now();
      }).length;
      const eventsPercent = totalEvents > 0 ? Math.round((pastEvents / totalEvents) * 100) : 0;
      const eventsProgressFill = courseView.querySelector('#events-progress-fill');
      const eventsCompletionText = courseView.querySelector('#events-completion-text');
      if (eventsProgressFill) {
        eventsProgressFill.style.width = `${eventsPercent}%`;
      }
      if (eventsCompletionText) {
        eventsCompletionText.textContent = `${eventsPercent}% of events completed`;
      }
      // Update pill active states - remove all first, then add to matching pill
      const pills = courseView.querySelectorAll('.course-section-card[aria-label="Events"] .filter-pill[data-mode]');
      pills.forEach(p => p.classList.remove('filter-pill-active'));
      pills.forEach(p => {
        if (p.dataset.mode === mode) {
          p.classList.add('filter-pill-active');
        }
      });
    }
    function refreshAssignments(){
      if(!assignmentsList) return;
      const mode = assignmentsFilter?.value || 'all';
      
      // Read from global array so we get the latest updates from toggle function
      const currentAssignments = window.scoutiqEffectiveAssignments || effectiveAssignments;
      
      // Check if assignment list is showing loading placeholder - always refresh if so
      const hasLoadingPlaceholder = assignmentsList.innerHTML.includes('panel-placeholder') && 
                                    assignmentsList.innerHTML.includes('Loading');
      
      // Check if a toggle just happened (within last 2000ms) - use optimized update
      // Prevents interference from React re-renders during animations
      const timeSinceLastToggle = Date.now() - (window.scoutiqLastToggleTime || 0);
      const recentToggle = timeSinceLastToggle < 2000;
      
      if (recentToggle && !hasLoadingPlaceholder) {
        console.log('[ScoutIQ Refresh] Using optimized update -', timeSinceLastToggle, 'ms since toggle');
        
        // Instead of full re-render, just update the progress bars and counts
        // The toggle function already updated the specific card's DOM
        
        // CRITICAL: Deduplicate before calculating to avoid counting duplicates
        const uniqueAssignments = new Map();
        currentAssignments.forEach(a => {
          const id = a.id || a.assignmentId || a.assignmentID || a.assignment_id;
          if (id) {
            uniqueAssignments.set(String(id), a);
          }
        });
        const deduplicatedAssignments = Array.from(uniqueAssignments.values());
        
        // Update counts using deduplicated data
        if(assignmentsCountsEl){
          const counts = tallyAssignments(deduplicatedAssignments);
          assignmentsCountsEl.textContent = `${counts.assignmentCompleted} completed, ${counts.assignmentRemaining} remaining`;
          
          // Update status chips
          const assignmentsStatusChips = courseView.querySelector('#assignments-status-chips');
          if (assignmentsStatusChips) {
            const totalAssignments = counts.assignmentCompleted + counts.assignmentRemaining;
            const overdueCount = deduplicatedAssignments.filter(a => {
              const dueRaw = a.dueDatetime || a.dueDate || a.dueAt || a.due_at || a.due;
              if (!dueRaw) return false;
              const dt = new Date(dueRaw.includes?.('T') ? dueRaw : dueRaw + 'T00:00:00');
              if (isNaN(dt)) return false;
              const isCompleted = a.completed === true || a.completed === 'true' || a.status === 'Completed' || a.submissionStatus === 'Completed';
              return dt < new Date() && !isCompleted;
            }).length;
            assignmentsStatusChips.innerHTML = `
              <span class="ca-status-chip">Done: ${counts.assignmentCompleted} / ${totalAssignments}</span>
              <span class="ca-status-chip">Overdue: ${overdueCount}</span>
            `;
          }
        }
        
        // Update progress bar smoothly
        const totalAssignments = deduplicatedAssignments.length;
        
        // Log actual completed values for debugging
        console.log('[ScoutIQ Refresh] Assignment completed values sample:', 
          deduplicatedAssignments.slice(0, 3).map(a => ({
            id: a.id || a.assignmentId,
            title: a.title?.substring(0, 30),
            completed: a.completed,
            completedType: typeof a.completed,
            status: a.status,
            submissionStatus: a.submissionStatus
          }))
        );
        
        const completedAssignments = deduplicatedAssignments.filter(a => {
          const isCompleted = a.completed === true || a.completed === 'true' || a.status === 'Completed' || a.submissionStatus === 'Completed';
          return isCompleted;
        }).length;
        const assignmentsPercent = totalAssignments > 0 ? Math.round((completedAssignments / totalAssignments) * 100) : 0;
        
        console.log('[ScoutIQ Refresh] Progress calculation (optimized path):', {
          totalInArray: currentAssignments.length,
          totalUnique: totalAssignments,
          hasDuplicates: currentAssignments.length !== totalAssignments,
          completed: completedAssignments,
          percent: assignmentsPercent
        });
        
        // Update the visible legacy progress bar
        const assignmentsProgressFill = courseView.querySelector('[data-legacy-bar="true"]');
        const assignmentsCompletionText = courseView.querySelector('[data-legacy-text="true"]');
        if (assignmentsProgressFill) {
          console.log('[ScoutIQ Refresh] Setting progress bar width:', {
            element: assignmentsProgressFill,
            beforeWidth: assignmentsProgressFill.style.width,
            newPercent: assignmentsPercent,
            newWidth: `${assignmentsPercent}%`
          });
          assignmentsProgressFill.style.width = `${assignmentsPercent}%`;
          console.log('[ScoutIQ Refresh] After setting width:', {
            styleWidth: assignmentsProgressFill.style.width,
            computedWidth: window.getComputedStyle(assignmentsProgressFill).width
          });
        } else {
          console.warn('[ScoutIQ Refresh] Could not find #assignments-progress-fill element');
        }
        if (assignmentsCompletionText) {
          assignmentsCompletionText.textContent = `${completedAssignments} of ${totalAssignments} assignments completed (${assignmentsPercent}%)`;
        }
        
        // Update the global assignment objects map without re-rendering
        if (!window.scoutiqAssignmentObjects) {
          window.scoutiqAssignmentObjects = new Map();
        }
        currentAssignments.forEach(assignment => {
          const id = assignment.id || assignment.assignmentId || assignment.assignmentID || assignment.assignment_id;
          if (id) {
            window.scoutiqAssignmentObjects.set(String(id), assignment);
          }
        });
        
        return; // Skip the full re-render to prevent flash
      }
      
      // Normal full refresh (no recent toggle)
      
      // Filter assignments based on mode
      const filtered = currentAssignments.filter(a => {
        if (mode === 'all') return true;
        const dueRaw = a.dueDatetime || a.dueDate || a.dueAt || a.due_at || a.due;
        if (!dueRaw) return mode !== 'overdue';
        const dt = new Date(dueRaw.includes?.('T') ? dueRaw : dueRaw + 'T00:00:00');
        if (isNaN(dt)) return mode !== 'overdue';
        const now = new Date();
        if (mode === 'upcoming') return dt >= now;
        if (mode === 'overdue') return dt < now && !(a.completed === true || a.completed === 'true' || a.status === 'Completed' || a.submissionStatus === 'Completed');
        return true;
      });
      
      // Always sort: incomplete first, then by due date within each group
      const sorted = filtered.sort((a,b)=>{
        const aCompleted = a.completed === true || a.completed === 'true' || a.status === 'Completed' || a.submissionStatus === 'Completed';
        const bCompleted = b.completed === true || b.completed === 'true' || b.status === 'Completed' || b.submissionStatus === 'Completed';
        if (aCompleted !== bCompleted) return aCompleted ? 1 : -1;
        const da = new Date((a.dueDatetime||a.dueDate||a.dueAt||a.due_at||a.due)||0).getTime();
        const db = new Date((b.dueDatetime||b.dueDate||b.dueAt||b.due_at||b.due)||0).getTime();
        return da - db;
      });
      
      // Check for recent toggle animation and loading placeholder
      const hasRecentToggle = window.scoutiqLastToggleTime && timeSinceLastToggle < 2000;
      const currentHasLoading = assignmentsList.innerHTML.includes('Loading');
      // Check if cards already exist in the DOM (not empty or loading)
      const hasExistingCards = assignmentsList.querySelector('.assignment-card[data-assignment-id]') !== null;
      
      // Render assignments - SKIP during user toggle animation ONLY if cards already exist
      // Always render if: no existing cards, loading placeholder, or no recent toggle
      if (hasRecentToggle && hasExistingCards && !currentHasLoading) {
        console.log('[ScoutIQ Refresh] Recent toggle animation - skipping innerHTML refresh to preserve transitions');
        // Don't touch the DOM - let CSS transitions handle everything
      } else {
        if (currentHasLoading) {
          console.log('[ScoutIQ Refresh] Loading placeholder - allowing refresh to clear it');
        }
        assignmentsList.innerHTML = sorted.length ? sorted.map(a => buildActivityCard(a,'assignment')).join('') : '<div class="panel-placeholder">No assignments</div>';
        
        // Wire up checkboxes
        wireAssignmentCheckboxes(assignmentsList);
      }
      
      // Store assignments globally for checkbox access
      if (!window.scoutiqAssignmentObjects) {
        window.scoutiqAssignmentObjects = new Map();
      }
      currentAssignments.forEach(assignment => {
        const id = assignment.id || assignment.assignmentId || assignment.assignmentID || assignment.assignment_id;
        if (id) {
          window.scoutiqAssignmentObjects.set(String(id), assignment);
        }
      });
      
      // Deduplicate before calculating counts and progress
      const uniqueAssignments = new Map();
      currentAssignments.forEach(a => {
        const id = a.id || a.assignmentId || a.assignmentID || a.assignment_id;
        if (id) {
          uniqueAssignments.set(String(id), a);
        }
      });
      const deduplicatedAssignments = Array.from(uniqueAssignments.values());
      
      // Update counts using deduplicated data
      if(assignmentsCountsEl){
        const counts = tallyAssignments(deduplicatedAssignments);
        assignmentsCountsEl.textContent = `${counts.assignmentCompleted} completed, ${counts.assignmentRemaining} remaining`;
        
        // Update status chips
        const assignmentsStatusChips = courseView.querySelector('#assignments-status-chips');
        if (assignmentsStatusChips) {
          const totalAssignments = counts.assignmentCompleted + counts.assignmentRemaining;
          const overdueCount = deduplicatedAssignments.filter(a => {
            const dueRaw = a.dueDatetime || a.dueDate || a.dueAt || a.due_at || a.due;
            if (!dueRaw) return false;
            const dt = new Date(dueRaw.includes?.('T') ? dueRaw : dueRaw + 'T00:00:00');
            if (isNaN(dt)) return false;
            const isCompleted = a.completed === true || a.completed === 'true' || a.status === 'Completed' || a.submissionStatus === 'Completed';
            return dt < new Date() && !isCompleted;
          }).length;
          assignmentsStatusChips.innerHTML = `
            <span class="ca-status-chip">Done: ${counts.assignmentCompleted} / ${totalAssignments}</span>
            <span class="ca-status-chip">Overdue: ${overdueCount}</span>
          `;
        }
      }
      
      // Update progress bar - using already deduplicated data
      const totalAssignments = deduplicatedAssignments.length;
      const completedAssignments = deduplicatedAssignments.filter(a => {
        const isCompleted = a.completed === true || a.completed === 'true' || a.status === 'Completed' || a.submissionStatus === 'Completed';
        return isCompleted;
      }).length;
      const assignmentsPercent = totalAssignments > 0 ? Math.round((completedAssignments / totalAssignments) * 100) : 0;
      
      // Update the visible legacy progress bar
      const assignmentsProgressFill = courseView.querySelector('[data-legacy-bar="true"]');
      const assignmentsCompletionText = courseView.querySelector('[data-legacy-text="true"]');
      if (assignmentsProgressFill) {
        assignmentsProgressFill.style.width = `${assignmentsPercent}%`;
      } else {
        console.warn('[ScoutIQ Refresh] Full refresh - Could not find legacy progress bar');
      }
      if (assignmentsCompletionText) {
        assignmentsCompletionText.textContent = `${completedAssignments} of ${totalAssignments} assignments completed (${assignmentsPercent}%)`;
      }
      
      // Update pill active states - remove all first, then add to matching pill
      const pills = courseView.querySelectorAll('.course-section-card[aria-label="Assignments"] .filter-pill[data-mode]');
      pills.forEach(p => p.classList.remove('filter-pill-active'));
      pills.forEach(p => {
        if (p.dataset.mode === mode) {
          p.classList.add('filter-pill-active');
        }
      });
    }
    eventsTimelineFilter?.addEventListener('change', refreshEvents);
    assignmentsFilter?.addEventListener('change', refreshAssignments);

    // === Wire pill clicks to hidden selects (no logic mutation) ===
    const eventPills = courseView.querySelectorAll('.course-section-card[aria-label="Events"] .filter-pill[data-mode]');
    eventPills.forEach(pill => {
      if(pill.dataset.bound === 'true') return; pill.dataset.bound = 'true';
      pill.addEventListener('click', () => {
        if(!eventsTimelineFilter) return;
        eventsTimelineFilter.value = pill.dataset.mode || 'upcoming';
        eventsTimelineFilter.dispatchEvent(new Event('change'));
      });
    });
    
    const assignmentPills = courseView.querySelectorAll('.course-section-card[aria-label="Assignments"] .filter-pill[data-mode]');
    assignmentPills.forEach(pill => {
      if(pill.dataset.bound === 'true') return; pill.dataset.bound = 'true';
      pill.addEventListener('click', () => {
        if(!assignmentsFilter) return;
        assignmentsFilter.value = pill.dataset.mode || 'all';
        assignmentsFilter.dispatchEvent(new Event('change'));
      });
    });
    
    // Store refresh functions globally so updates can trigger re-renders
    window.scoutiqRefreshAssignments = refreshAssignments;
    window.scoutiqRefreshEvents = refreshEvents;
    
    // Set up a mutation observer to detect when React clears the events list
    // and automatically re-populate it
    const setupEventsListObserver = () => {
      if (!eventsList) return;
      
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          // Check if the list was cleared (childList mutation with removed nodes)
          if (mutation.type === 'childList' && mutation.removedNodes.length > 0) {
            const hasEventCards = eventsList.querySelector('.event-card[data-event-id]');
            if (!hasEventCards && window.scoutiqEffectiveEvents && window.scoutiqEffectiveEvents.length > 0) {
              console.log('[ScoutIQ Observer] Events list cleared but we have event data, re-rendering');
              setTimeout(() => refreshEvents(), 0);
            }
          }
        }
      });
      
      observer.observe(eventsList, {
        childList: true,
        subtree: false
      });
      
      // Store observer for cleanup
      if (!window.scoutiqObservers) window.scoutiqObservers = [];
      window.scoutiqObservers.push(observer);
    };
    
    setupEventsListObserver();
    
    // Listen for event updates to refresh the event cards with new data
    // Use a global handler to make it idempotent
    if (!window.scoutiqEventUpdateHandler) {
      window.scoutiqEventUpdateHandler = (e) => {
        const { eventId, updatedEvent, sentData } = e.detail || {};
        if (!eventId) return;
        
        console.log('[ScoutIQ Event Update] Received update for event:', eventId, {
          serverResponse: updatedEvent,
          sentData: sentData
        });
        
        // Update the event in the global array
        if (window.scoutiqEffectiveEvents) {
          const eventIndex = window.scoutiqEffectiveEvents.findIndex(ev => {
            const id = ev.id || ev.eventId || ev.eventID || ev.event_id;
            return String(id) === String(eventId);
          });
          
          if (eventIndex !== -1 && updatedEvent) {
            // WORKAROUND: Backend has timezone conversion bug
            // If we sent datetime fields, use our values instead of server's response
            const mergedEvent = { ...window.scoutiqEffectiveEvents[eventIndex], ...updatedEvent };
            
            // Override with sent datetime values if they were updated
            if (sentData) {
              if (sentData.start) {
                mergedEvent.startDatetime = sentData.start;
                console.log('[ScoutIQ Event Update] Using sent start datetime instead of server response:', sentData.start);
              }
              if (sentData.end) {
                mergedEvent.endDatetime = sentData.end;
                console.log('[ScoutIQ Event Update] Using sent end datetime instead of server response:', sentData.end);
              }
            }
            
            window.scoutiqEffectiveEvents[eventIndex] = mergedEvent;
            console.log('[ScoutIQ Event Update] Updated event in array:', eventId, {
              startDatetime: window.scoutiqEffectiveEvents[eventIndex].startDatetime,
              endDatetime: window.scoutiqEffectiveEvents[eventIndex].endDatetime
            });
          }
        }
        
        // Refresh the event cards to show the updated time
        // Use requestAnimationFrame to ensure DOM is ready, with retry logic
        const attemptRefresh = (attempt = 1, maxAttempts = 5) => {
          if (window.scoutiqRefreshEvents) {
            console.log('[ScoutIQ Event Update] Attempting to refresh event cards (attempt', attempt, ')');
            
            // Check if the events list container exists
            const eventsList = document.querySelector('#course-events-list');
            if (!eventsList) {
              console.warn('[ScoutIQ Event Update] Events list container not found, retrying...');
              if (attempt < maxAttempts) {
                setTimeout(() => attemptRefresh(attempt + 1, maxAttempts), 100);
              } else {
                console.error('[ScoutIQ Event Update] Failed to find events list after', maxAttempts, 'attempts');
              }
              return;
            }
            
            window.scoutiqRefreshEvents();
            
            // Verify the refresh worked
            setTimeout(() => {
              const renderedCards = eventsList.querySelectorAll('.event-card[data-event-id]');
              console.log('[ScoutIQ Event Update] Post-refresh check:', {
                cardsFound: renderedCards.length,
                containerHTML: eventsList.innerHTML.substring(0, 200)
              });
            }, 50);
          }
        };
        
        requestAnimationFrame(() => attemptRefresh());
      };
      
      // Register the listener only once
      window.addEventListener('scoutiq:eventUpdated', window.scoutiqEventUpdateHandler);
      console.log('[ScoutIQ] Registered global event update listener');
    }
    
    // Listen for assignment updates (completion, edits, deletes) to refresh assignment cards
    if (!window.scoutiqAssignmentUpdateHandler) {
      window.scoutiqAssignmentUpdateHandler = (e) => {
        const { assignmentId, updatedAssignment, action } = e.detail || {};
        if (!assignmentId) return;
        
        console.log('[ScoutIQ Assignment Update] Received update for assignment:', assignmentId, {
          action,
          updatedAssignment
        });
        
        // Update the assignment in the global array
        if (window.scoutiqEffectiveAssignments) {
          const assignmentIndex = window.scoutiqEffectiveAssignments.findIndex(a => {
            const id = a.id || a.assignmentId || a.assignmentID || a.assignment_id;
            return String(id) === String(assignmentId);
          });
          
          if (assignmentIndex !== -1 && updatedAssignment) {
            window.scoutiqEffectiveAssignments[assignmentIndex] = {
              ...window.scoutiqEffectiveAssignments[assignmentIndex],
              ...updatedAssignment
            };
            console.log('[ScoutIQ Assignment Update] Updated assignment in array:', assignmentId);
          } else if (action === 'deleted' && assignmentIndex !== -1) {
            // Remove deleted assignment
            window.scoutiqEffectiveAssignments.splice(assignmentIndex, 1);
            console.log('[ScoutIQ Assignment Update] Removed deleted assignment from array:', assignmentId);
          }
        }
        
        // Refresh the assignment cards (but skip if this update was from a recent checkbox toggle)
        // The toggle already optimistically updated the UI, so we don't want to re-render
        const timeSinceLastToggle = Date.now() - (window.scoutiqLastToggleTime || 0);
        const recentToggle = timeSinceLastToggle < 400;
        
        if (window.scoutiqRefreshAssignments && !recentToggle) {
          console.log('[ScoutIQ Assignment Update] Refreshing assignment cards');
          requestAnimationFrame(() => {
            window.scoutiqRefreshAssignments();
          });
        } else if (recentToggle) {
          console.log('[ScoutIQ Assignment Update] Skipping refresh - recent toggle at', timeSinceLastToggle, 'ms ago');
        }
      };
      
      // Register the listener only once
      window.addEventListener('scoutiq:assignmentUpdated', window.scoutiqAssignmentUpdateHandler);
      console.log('[ScoutIQ] Registered global assignment update listener');
    }
    
    // Register force refresh listener (called after animations complete)
    if (!window.scoutiqForceRefreshHandler) {
      window.scoutiqForceRefreshHandler = () => {
        console.log('[ScoutIQ Force Refresh] Force refresh event received, refreshing assignments');
        if (window.scoutiqRefreshAssignments) {
          window.scoutiqRefreshAssignments();
        }
      };
      window.addEventListener('scoutiq:forceRefresh', window.scoutiqForceRefreshHandler);
      console.log('[ScoutIQ] Registered force refresh listener');
    }
    
    // Initialize filter defaults before refreshing
    if (eventsTimelineFilter) eventsTimelineFilter.value = 'upcoming';
    if (assignmentsFilter) assignmentsFilter.value = 'all';
    
    // Clear any stray active classes from previous renders
    const allFilterPills = courseView.querySelectorAll('.filter-pill');
    allFilterPills.forEach(p => p.classList.remove('filter-pill-active'));
    
    refreshEvents();
    refreshAssignments();
    // Glance summary removed.
    if (calendarContainer) {
      calendarContainer.innerHTML = '';
      calendarContainer.insertAdjacentHTML('beforeend','<h3 class="course-subheading">This Course Calendar</h3>');
      allEventCards.forEach(card => calendarContainer.appendChild(card.cloneNode(true)));
      ensureNonEmpty(calendarContainer, 'Nothing scheduled yet for this course.');
    }

    if (syllabusContainer) {
      syllabusContainer.innerHTML='';
      const sourceCard = document.querySelector(`.scoutiq-course-card[data-course-id="${CSS.escape(courseId)}"]`);
      const syllabusDocId = sourceCard?.dataset?.courseSyllabusDocId;
      const syllabusDocName = sourceCard?.dataset?.courseSyllabusDocName || 'Syllabus';
      if (syllabusDocId) {
        syllabusContainer.insertAdjacentHTML('beforeend', buildSyllabusPanel(syllabusDocId, syllabusDocName));
        // Wire up the action buttons
        wireSyllabusButtons(syllabusContainer);
      } else {
        // Create empty preview container even when no syllabus, so portal can still mount (and show empty state)
        syllabusContainer.insertAdjacentHTML('beforeend', `
          <section class="syllabus-section">
            <div class="syllabus-viewer-wrapper">
              <div class="syllabus-preview-container" 
                   data-portal-ready="true"
                   data-syllabus-doc-id=""
                   data-syllabus-doc-name="">
                <div class="panel-placeholder">No syllabus uploaded yet.</div>
              </div>
            </div>
          </section>
        `);
      }
    }

    // Documents container – keep minimal placeholder for React portal mounting
    const docsContainer = courseView.querySelector('#scoutiq-course-docs-container');
    if (docsContainer) {
      // Start with a placeholder that will be replaced by React portal
      docsContainer.innerHTML = '<div class="panel-placeholder">Loading documents...</div>';
      // Add a data attribute to help with portal detection
      docsContainer.setAttribute('data-course-id', courseId);
      docsContainer.setAttribute('data-portal-ready', 'true');
    }
  } catch (e) {
    console.warn('ScoutIQ: population error', e);
  }

  // Bind vanilla edit/delete controls into the rendered view
  try { bindControlsAfterRender(course); } catch (e) { /* non-fatal */ }
  
  // Only initialize tabs if this is a new course view, otherwise skip to preserve calendar state
  if (!isDataUpdate) {
    initScoutIQCourseTabs(courseView, activeTabName);
    initDescriptionToggle();
  } else if (activeTabName) {
    // For data updates, just restore the active tab without reinitializing
    const tabs = Array.from(courseView.querySelectorAll('.course-tab'));
    const tabToActivate = tabs.find(t => t.dataset.tab === activeTabName);
    if (tabToActivate && !tabToActivate.classList.contains('is-active')) {
      tabs.forEach(t => {
        const isActive = t === tabToActivate;
        t.classList.toggle('is-active', isActive);
        t.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      const panels = Array.from(courseView.querySelectorAll('.course-tab-panel'));
      panels.forEach(panel => {
        panel.classList.toggle('is-active', panel.dataset.tab === activeTabName);
      });
    }
  }
}

// Setup listeners on existing course cards; idempotent (safe to call multiple times)
export function setupVanillaCourseView() {
  const cards = document.querySelectorAll('.scoutiq-course-card[data-course-id]');
  if (!cards.length) return;
  cards.forEach(card => {
    if (card.dataset.hasVanillaHandler === 'true') return; // idempotent guard
    card.dataset.hasVanillaHandler = 'true';
    card.addEventListener('click', () => {
      const courseData = {
        id: card.dataset.courseId,
        title: card.dataset.courseTitle,
        location: card.dataset.courseLocation,
        dates: card.dataset.courseDates,
        professor: card.dataset.courseProfessor,
        description: card.dataset.courseDescription,
        officeHours: card.dataset.officeHours || card.dataset.courseOfficeHours || '',
        courseCode: card.dataset.courseCode || ''
      };
      showScoutIQCourseView(courseData);
    });
  });
}

function ensureNonEmpty(container, message) {
  if (!container) return;
  if (!container.children.length) {
    container.innerHTML = `<p class="course-empty-state">${message}</p>`;
  }
}

// === Vanilla helpers: navigation, left-card lookup, edit/delete controls ===
function getLeftCourseCardEl(id) {
  if (!id) return null;
  const byEndeavor = document.querySelector(`[data-endeavor-id="${CSS.escape(String(id))}"]`);
  if (byEndeavor) return byEndeavor;
  return document.querySelector(`[data-course-id="${CSS.escape(String(id))}"]`);
}

function injectCourseControls(root) {
  if (!root) return;
  // Inject minimal styles for React portal
  if (!document.getElementById('scoutiq-course-info-styles')) {
    const style = document.createElement('style');
    style.id = 'scoutiq-course-info-styles';
    style.innerHTML = `
      .course-info-card { position: relative; overflow: visible !important; }
      #course-info-react-portal { position: relative; z-index: 1; overflow: visible !important; }
      .course-info-react-embed { position: relative; overflow: visible !important; }
      .course-details-container { overflow: visible !important; }
      .course-info-card::after { content: ""; display: table; clear: both; }
    `;
    document.head.appendChild(style);
  }

  // Bind delete handler to the header delete icon
  const deleteBtn = root.querySelector('#course-header-delete-btn');
  if (deleteBtn && deleteBtn.dataset.bound !== 'true') {
    deleteBtn.dataset.bound = 'true';
    deleteBtn.addEventListener('click', async () => {
      const courseView = document.getElementById('scoutiq-course-view');
      const rootCourseId = String(courseView?.dataset?.courseId || '');
      const confirmed = (window.showConfirmModal) ? await new Promise(resolve => {
        window.showConfirmModal({ title: 'Delete this course?', body: "This will remove the course, its events, assignments, and connected documents. This can\u2019t be undone.", confirmText: 'Delete', onConfirm: () => resolve(true), onCancel: () => resolve(false) });
      }) : window.confirm("Delete this course? This will remove the course, its events, assignments, and connected documents. This can\u2019t be undone.");
      if (!confirmed) return;
      try {
        // Use removeEndeavor for optimistic React state update (updates coursesById immediately)
        if (window.dataContextInstance?.removeEndeavor) {
          await window.dataContextInstance.removeEndeavor(rootCourseId);
        } else {
          // Fallback: direct API call + manual cache clear
          const response = await EndeavorApi.deleteEndeavor(rootCourseId);
          if (!response.ok) throw new Error(`Delete failed with status ${response.status}`);
          if (window.dataContextInstance?.clearCache) {
            const clearFn = window.dataContextInstance.clearCache;
            clearFn(`endeavor_${rootCourseId}`);
            clearFn(`endeavor-events_${rootCourseId}`);
            clearFn(`endeavor-assignments_${rootCourseId}`);
          }
        }
        if (window.dataContextInstance?.addToast) window.dataContextInstance.addToast({ intent: 'success', message: 'Course deleted' });
        try { window.dispatchEvent(new CustomEvent('scoutiq:clearSelection')); } catch (e) { /* */ }
      } catch (err) {
        console.error('Failed to delete course:', err);
        if (window.dataContextInstance?.addToast) window.dataContextInstance.addToast({ intent: 'error', message: 'Could not delete course', duration: 6000 });
      }
    });
  }
}

function bindControlsAfterRender(course) {
  const root = document.getElementById('scoutiq-course-view');
  if (!root) return;
  root.dataset.courseId = String(course.id || course.endeavorId || '');
  const headerTitle = root.querySelector('.course-view-title');
  if (headerTitle) headerTitle.dataset.courseId = String(course.id || course.endeavorId || '');
  injectCourseControls(root);
}

function markActiveCourseCard(courseId) {
  const cards = document.querySelectorAll('.scoutiq-course-card');
  cards.forEach(card => {
    card.classList.toggle('is-active', card.dataset.courseId === courseId);
  });
}

// eslint-disable-next-line no-unused-vars
function formatFullDate(value){
  if(!value) return '';
  try {
    const d = new Date(value.includes('T')? value : value+'T00:00:00');
    if(isNaN(d)) return value;
    return d.toLocaleDateString('en-US',{ year:'numeric', month:'long', day:'numeric' });
  } catch { return value; }
}

// Initializes expand/collapse behavior for the course info section.
function initDescriptionToggle(root = document.getElementById('scoutiq-course-view')) {
  if (!root) {
    console.log('[ScoutIQ] Root not found for description toggle');
    return;
  }
  const infoSection = root.querySelector('.course-info-section');
  const toggleBtn = root.querySelector('.course-info-toggle');
  if (!infoSection || !toggleBtn) {
    console.log('[ScoutIQ] Info section or toggle button not found');
    return;
  }
  if (toggleBtn.dataset.bound === 'true') return; // idempotent
  toggleBtn.dataset.bound = 'true';

  function setState(state) {
    infoSection.dataset.state = state;
    toggleBtn.dataset.state = state;
    const expanded = state === 'expanded';
    
    // Update ARIA attributes for accessibility
    toggleBtn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    toggleBtn.setAttribute('title', expanded ? 'Hide course info' : 'Show course info');
    // Add/remove active class for visual feedback on the gear icon
    toggleBtn.classList.toggle('is-active', expanded);
  }

  toggleBtn.addEventListener('click', () => {
    const next = infoSection.dataset.state === 'expanded' ? 'collapsed' : 'expanded';
    setState(next);
  });

  // Header title click — expand panel and start title editing
  const headerTitle = root.querySelector('.course-view-title');
  if (headerTitle && headerTitle.dataset.bound !== 'true') {
    headerTitle.dataset.bound = 'true';
    headerTitle.style.cursor = 'pointer';
    headerTitle.addEventListener('click', () => {
      if (infoSection.dataset.state !== 'expanded') {
        setState('expanded');
      }
      setTimeout(() => {
        const titleClickable = root.querySelector('.course-title-embedded.clickable');
        if (titleClickable) {
          titleClickable.click();
        }
      }, 180);
    });
  }

  // Set up initial accessibility attributes
  toggleBtn.setAttribute('aria-controls', 'course-info-section');
  infoSection.setAttribute('id', 'course-info-section');
  
  // Default collapsed
  setState('collapsed');
}


function buildSyllabusPanel(docId, docName){
  const safeDocName = escapeHtml(docName || 'Syllabus');
  // Compact header row + full-height PDF viewer (no card-in-card, no blue download bar)
  return `<section class="syllabus-section">
    <div class="syllabus-header-row">
      <div class="syllabus-header-left">
        <svg class="syllabus-file-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
        </svg>
        <span class="syllabus-title">${safeDocName}</span>
      </div>
      <div class="syllabus-header-actions">
        <button class="syllabus-action-btn syllabus-btn-secondary" type="button" data-action="open-new-tab" data-doc-id="${escapeHtml(docId)}" title="Open in new tab">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
            <polyline points="15 3 21 3 21 9"></polyline>
            <line x1="10" y1="14" x2="21" y2="3"></line>
          </svg>
          <span class="btn-label">Open</span>
        </button>
        <button class="syllabus-action-btn syllabus-btn-secondary" type="button" data-action="download" data-doc-id="${escapeHtml(docId)}" data-doc-name="${safeDocName}" title="Download">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          <span class="btn-label">Download</span>
        </button>
        <button class="syllabus-action-btn syllabus-btn-maximize" type="button" data-action="maximize" title="Maximize viewer" aria-label="Toggle maximize">
          <svg class="maximize-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="15 3 21 3 21 9"></polyline>
            <polyline points="9 21 3 21 3 15"></polyline>
            <line x1="21" y1="3" x2="14" y2="10"></line>
            <line x1="3" y1="21" x2="10" y2="14"></line>
          </svg>
          <svg class="minimize-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="display:none">
            <polyline points="4 14 10 14 10 20"></polyline>
            <polyline points="20 10 14 10 14 4"></polyline>
            <line x1="14" y1="10" x2="21" y2="3"></line>
            <line x1="3" y1="21" x2="10" y2="14"></line>
          </svg>
        </button>
      </div>
    </div>
    <div class="syllabus-viewer-wrapper">
      <div class="syllabus-preview-container" 
           data-syllabus-doc-id="${escapeHtml(docId)}" 
           data-syllabus-doc-name="${safeDocName}"
           data-portal-ready="true">
        <div class="panel-placeholder">Loading syllabus preview...</div>
      </div>
    </div>
  </section>`;
}

// Wire up syllabus action buttons (open in new tab, download, maximize) + search
function wireSyllabusButtons(container) {
  if (!container) return;
  
  const openBtn = container.querySelector('.syllabus-action-btn[data-action="open-new-tab"]');
  const downloadBtn = container.querySelector('.syllabus-action-btn[data-action="download"]');
  const maximizeBtn = container.querySelector('.syllabus-action-btn[data-action="maximize"]');
  
  if (openBtn && openBtn.dataset.bound !== 'true') {
    openBtn.dataset.bound = 'true';
    openBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const docId = openBtn.getAttribute('data-doc-id');
      if (docId) {
        // Open raw PDF URL directly (not the app route)
        const baseUrl = window.location.origin;
        const url = `${baseUrl}/api/documents/${docId}/download`;
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    });
  }
  
  if (downloadBtn && downloadBtn.dataset.bound !== 'true') {
    downloadBtn.dataset.bound = 'true';
    downloadBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      const docId = downloadBtn.getAttribute('data-doc-id');
      const docName = downloadBtn.getAttribute('data-doc-name') || 'Syllabus';
      if (docId) {
        try {
          const baseUrl = window.location.origin;
          const link = document.createElement('a');
          link.href = `${baseUrl}/api/documents/${docId}/download`;
          link.download = docName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } catch (error) {
          console.error('Error downloading syllabus:', error);
        }
      }
    });
  }

  // Maximize toggle
  if (maximizeBtn && maximizeBtn.dataset.bound !== 'true') {
    maximizeBtn.dataset.bound = 'true';
    const STORAGE_KEY = 'scoutiq:syllabusMaximized';

    // Restore persisted maximize state
    const restoredMax = localStorage.getItem(STORAGE_KEY) === 'true';
    if (restoredMax) {
      applySyllabusMaximize(true, maximizeBtn);
    }

    maximizeBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const section = maximizeBtn.closest('.syllabus-section');
      const isMax = section?.classList.contains('is-maximized');
      applySyllabusMaximize(!isMax, maximizeBtn);
    });
  }
}

/** Apply or remove syllabus maximize state */
function applySyllabusMaximize(maximize, maximizeBtn) {
  const STORAGE_KEY = 'scoutiq:syllabusMaximized';
  const section = maximizeBtn?.closest('.syllabus-section');
  const courseView = document.getElementById('scoutiq-course-view');
  const grid = document.querySelector('.scoutiq-dashboard-grid');
  const tabsRow = courseView?.querySelector('.course-tabs');

  if (maximize) {
    section?.classList.add('is-maximized');
    courseView?.classList.add('syllabus-maximized');
    grid?.classList.add('syllabus-reader-mode');
    tabsRow?.classList.add('is-hidden');
    // Collapse courses sidebar via custom event
    window.dispatchEvent(new CustomEvent('scoutiq:collapseCourses', { detail: { collapse: true } }));
    // Toggle icons
    const maxIcon = maximizeBtn?.querySelector('.maximize-icon');
    const minIcon = maximizeBtn?.querySelector('.minimize-icon');
    if (maxIcon) maxIcon.style.display = 'none';
    if (minIcon) minIcon.style.display = '';
  } else {
    section?.classList.remove('is-maximized');
    courseView?.classList.remove('syllabus-maximized');
    grid?.classList.remove('syllabus-reader-mode');
    tabsRow?.classList.remove('is-hidden');
    // Expand courses sidebar back
    window.dispatchEvent(new CustomEvent('scoutiq:collapseCourses', { detail: { collapse: false } }));
    const maxIcon = maximizeBtn?.querySelector('.maximize-icon');
    const minIcon = maximizeBtn?.querySelector('.minimize-icon');
    if (maxIcon) maxIcon.style.display = '';
    if (minIcon) minIcon.style.display = 'none';
  }

  // Persist
  try { localStorage.setItem(STORAGE_KEY, maximize ? 'true' : 'false'); } catch(_) {}
}

// Convert a .scoutiq-event-card into CourseView-styled .event-card markup
// eslint-disable-next-line no-unused-vars
function buildEventCardFromScoutCard(card) {
  const title = card.querySelector('.scoutiq-course-upcoming-card__title')?.textContent || 'Untitled Event';
  const dateText = card.querySelector('.scoutiq-course-upcoming-card__date')?.textContent || '';
  const pill = card.querySelector('.scoutiq-course-upcoming-card__pill')?.textContent || '';
  return `<div class="event-card" data-course-id="${card.dataset.courseId||''}" role="group" aria-label="${escapeHtml(title)}">
    <div class="event-icon" aria-hidden="true">📅</div>
    <div class="event-details">
      <h4>${escapeHtml(title)}</h4>
      ${pill ? `<div class="event-type-badge">${escapeHtml(pill)}</div>` : ''}
      <div class="event-date">${escapeHtml(dateText)}</div>
    </div>
  </div>`;
}

// Convert a .scoutiq-assignment-card into CourseView-styled .assignment-card markup
// eslint-disable-next-line no-unused-vars
function buildAssignmentCardFromScoutCard(card) {
  const title = card.querySelector('.scoutiq-course-upcoming-card__title')?.textContent || 'Untitled Assignment';
  const dateText = card.querySelector('.scoutiq-course-upcoming-card__date')?.textContent || '';
  const pill = card.querySelector('.scoutiq-course-upcoming-card__pill')?.textContent || '';
  return `<div class="assignment-card" data-course-id="${card.dataset.courseId||''}" role="group" aria-label="${escapeHtml(title)}">
    <div class="assignment-icon" aria-hidden="true">📝</div>
    <div class="assignment-details">
      <h4>${escapeHtml(title)}</h4>
      ${pill ? `<div class="status-badge pending">${escapeHtml(pill)}</div>` : ''}
      <div class="assignment-due">${escapeHtml(dateText)}</div>
    </div>
  </div>`;
}

// Date extraction helper from fallback DOM cards (attempts to parse inner date text)
function extractDateAttr(card){
  const txt = card.querySelector('.scoutiq-course-upcoming-card__date')?.textContent || '';
  if(!txt) return '';
  // Try parse common formats (e.g., 'Due: Tue, Nov 14, 4:30 PM')
  const cleaned = txt.replace(/^Due:\s*/i,'').trim();
  return cleaned; // leave formatting; we treat string w/ Date constructor attempt later
}

// Wide card builder for new columns
// New activity card builder using existing assignment/event inline card styling for parity
function buildActivityCard(item,type){
  // Unified fallbacks + broader field coverage to avoid blank cards.
  const baseTitle = item.title || item.name || item.assignmentName || item.eventName || item.endeavorName || item.taskName || item.label || 'Untitled';
  const title = escapeHtml(baseTitle);
  const rawDate = item.startDatetime || item.dueDatetime || item.dueDate || item.dueAt || item.due_at || item.due || item.date || item.timestamp || '';
  const dateDisplay = rawDate ? formatInlineDate(rawDate) : 'NO DUE DATE';
  if(type==='event'){
    // Extract event ID - try multiple possible field names
    const eventId = item.id || item.eventId || item.eventID || item.event_id || item.calendarEventId || item.endeavorEventId || '';
    
    if (!eventId) {
      console.warn('[ScoutIQ buildActivityCard] Event missing ID:', {
        item,
        title: baseTitle,
        allKeys: Object.keys(item),
        sampleFields: {
          id: item.id,
          eventId: item.eventId,
          eventID: item.eventID,
          event_id: item.event_id,
          calendarEventId: item.calendarEventId,
          endeavorEventId: item.endeavorEventId
        }
      });
    }
    
    return `<div class="course-item-card event-card event-card--${deriveEventStatus(rawDate)}" role="group" aria-label="${title}" data-event-id="${escapeHtml(String(eventId))}">
      <div class="activityTextCol">
        <div class="event-card-title" title="${title}">${title}</div>
        <div class="event-card-meta" title="${escapeHtml(dateDisplay)}">${escapeHtml(dateDisplay)}</div>
      </div>
    </div>`;
  }
  
  // Extract ID using the same logic as Overview Dashboard (CenterColumn.js)
  const assignmentId = item.id || item.assignmentId || item.assignmentID || item.assignment_id || '';
  
  if (!assignmentId) {
    console.warn('[ScoutIQ buildActivityCard] Assignment missing ID:', {
      item,
      title: baseTitle,
      allKeys: Object.keys(item)
    });
  }
  
  const statusClass = deriveAssignmentStatus(item, rawDate);
  const checkedAttr = statusClass === 'completed' ? 'checked' : '';
  return `<div class="course-item-card assignment-card assignment-card--${statusClass}" role="group" aria-label="${title}" data-assignment-id="${escapeHtml(String(assignmentId))}">
    <div class="assignment-card-content">
      <div class="activityTextCol">
        <div class="assignment-card-title" title="${title}">${title}</div>
        <div class="assignment-card-meta" title="${escapeHtml(dateDisplay)}">${escapeHtml(dateDisplay)}</div>
      </div>
      <label class="assignment-card-checkbox" aria-label="Mark ${title} complete">
        <input type="checkbox" ${checkedAttr} />
        <span class="assignment-card-checkbox-ui"></span>
      </label>
    </div>
  </div>`;
}

function deriveEventStatus(raw){
  if(!raw) return 'default';
  try { const d = new Date(raw); if(isNaN(d)) return 'default'; return d.getTime() < Date.now() ? 'past' : 'upcoming'; } catch { return 'default'; }
}
function deriveAssignmentStatus(item, raw){
  const completed = item && (item.completed || item.status === 'Completed' || item.submissionStatus === 'Completed');
  if(completed) return 'completed';
  if(!raw) return 'default';
  try { const d = new Date(raw); if(isNaN(d)) return 'default'; return d.getTime() < Date.now() ? 'overdue' : 'upcoming'; } catch { return 'default'; }
}
// eslint-disable-next-line no-unused-vars
function renderAssignmentChip(item, raw){
  const status = deriveAssignmentStatus(item, raw);
  if(status==='default') return '';
  const label = status==='completed' ? 'Done' : (status==='overdue' ? 'Overdue' : 'Upcoming');
  return `<span class="assignment-chip assignment-chip--${status}">${label}</span>`;
}

function formatInlineDate(raw){
  if(!raw) return 'No date';
  try {
    const d = new Date(raw);
    if(isNaN(d)) return raw;
    return d.toLocaleDateString(undefined,{month:'short', day:'numeric'}) + ' ' + d.toLocaleTimeString([], {hour:'numeric', minute:'2-digit'});
  } catch { return raw; }
}

// Legacy wide date formatter retained (may be used elsewhere); prefer formatInlineDate for new cards.
// eslint-disable-next-line no-unused-vars
function formatWideDate(raw){
  if(!raw) return 'No date';
  try { const attempt = new Date(raw); if(isNaN(attempt)) return raw; return attempt.toLocaleDateString('en-US',{month:'short',day:'numeric'}) + ' ' + attempt.toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'}); } catch { return raw; }
}

function tallyAssignments(list){
  let assignmentCompleted=0; 
  list.forEach(a=>{ 
    if(a.completed === true || a.completed === 'true' || a.status === 'Completed' || a.submissionStatus === 'Completed') assignmentCompleted++; 
  });
  return { assignmentCompleted, assignmentRemaining: list.length - assignmentCompleted };
}
function tallyEvents(list){
  const now = Date.now(); let eventPast=0; let eventUpcoming=0;
  list.forEach(ev=>{ const raw = ev.startDatetime; if(!raw){ eventUpcoming++; return; } const dt = new Date(raw.includes('T')? raw : raw); if(isNaN(dt)) { eventUpcoming++; return; } if(dt.getTime()<now) eventPast++; else eventUpcoming++; });
  return { eventPast, eventUpcoming };
}
function filterEvent(ev,mode){
  if(mode==='all') return true; const raw=ev.startDatetime; if(!raw) return mode==='upcoming'; const dt=new Date(raw.includes('T')? raw:raw); if(isNaN(dt)) return mode==='upcoming'; const now=new Date(); return mode==='past'? dt<now : dt>=now; }
// eslint-disable-next-line no-unused-vars
function filterAssignment(a,status,due){
  const completed = a.completed; if(status==='complete' && !completed) return false; if(status==='incomplete' && completed) return false; if(due!=='all'){ const raw=a.dueDatetime; if(!raw) return due!=='overdue'; const dt=new Date(raw.includes('T')? raw:raw); if(isNaN(dt)) return due!=='overdue'; const now=new Date(); if(due==='upcoming' && dt<now) return false; if(due==='overdue' && (dt>=now || completed)) return false; } return true; }

function escapeHtml(str){
  return String(str).replace(/[&<>"']/g, s => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;','\'':'&#39;'}[s]));
}

// Group items by app/source name for glance summary
// eslint-disable-next-line no-unused-vars
function groupByApp(items){
  const groups = {};
  items.forEach(it => {
    const key = (it.app || 'General').trim() || 'General';
    if(!groups[key]) groups[key] = [];
    groups[key].push(it);
  });
  return groups;
}

// eslint-disable-next-line no-unused-vars
function buildGlanceHTML(groupMap){
  const keys = Object.keys(groupMap);
  if(!keys.length) return '';
  return `<div class="course-glance-groups">${keys.map(k => {
    const items = groupMap[k];
    return `<div class="course-glance-group" data-app="${escapeHtml(k)}">
      <h4 class="course-glance-group__title">${escapeHtml(k)}</h4>
      <ul class="course-glance-list">${items.map(it => `<li class="course-glance-item">
        <span class="cg-type-badge ${it.type==='Assignment' ? 'is-assignment':'is-event'}">${escapeHtml(it.type)}</span>
        <span class="cg-item-title">${escapeHtml(it.title)}</span>
        ${it.dueDate ? `<span class="cg-item-date">${escapeHtml(formatShortGlanceDate(it.dueDate))}</span>` : ''}
      </li>`).join('')}</ul>
    </div>`;
  }).join('')}</div>`;
}

function formatShortGlanceDate(val){
  try {
    // Accept already formatted textual date from fallback cards
    const maybeDate = new Date(val);
    if(!isNaN(maybeDate)) {
      return maybeDate.toLocaleDateString(undefined,{ month:'short', day:'numeric' });
    }
    return String(val);
  } catch { return String(val); }
}

// Optional: expose globally for manual triggering/testing
if (typeof window !== 'undefined') {
  window.scoutiqCourseView = { setupVanillaCourseView, showScoutIQCourseView };
  
  // Diagnostic function to check assignment checkbox setup
  window.scoutiqDebug = {
    checkAssignmentSetup: () => {
      console.log('=== ScoutIQ Assignment Checkbox Diagnostic ===');
      console.log('DataContext available:', !!window.dataContextInstance);
      console.log('Toggle function available:', typeof window.scoutiqAssignmentToggle);
      
      if (window.dataContextInstance) {
        console.log('DataContext methods:', {
          markAssignmentCompleted: typeof window.dataContextInstance.markAssignmentCompleted,
          updateAssignment: typeof window.dataContextInstance.updateAssignment
        });
      }
      
      const assignmentCards = document.querySelectorAll('.assignment-card[data-assignment-id]');
      console.log('Assignment cards found:', assignmentCards.length);
      
      assignmentCards.forEach((card, idx) => {
        const id = card.dataset.assignmentId;
        const checkbox = card.querySelector('input[type="checkbox"]');
        const title = card.querySelector('.assignment-card-title')?.textContent;
        console.log(`Card ${idx}:`, {
          id,
          title,
          hasCheckbox: !!checkbox,
          checkboxBound: checkbox?.dataset?.bound,
          checkboxChecked: checkbox?.checked,
          checkboxDisabled: checkbox?.disabled
        });
      });
      
      console.log('=== End Diagnostic ===');
    },
    testAssignmentToggle: async (assignmentId, completed = true) => {
      console.log('=== Testing Assignment Toggle ===');
      console.log('ID:', assignmentId, 'Completed:', completed);
      try {
        if (typeof window.scoutiqAssignmentToggle === 'function') {
          await window.scoutiqAssignmentToggle(assignmentId, completed);
          console.log('✅ Toggle successful');
        } else {
          console.error('❌ Toggle function not available');
        }
      } catch (e) {
        console.error('❌ Toggle failed:', e);
      }
      console.log('=== End Test ===');
    }
  };
}

// Function to trigger smooth completion animation:
// Strikethrough + fade + collapse all happen in one fluid motion.
// Once invisible, DOM moves to bottom and refresh paints the final state.
function triggerConfettiAnimation(assignmentCard) {
  try {
    window.scoutiqAnimatingAssignmentId = assignmentCard.dataset.assignmentId;
    window.scoutiqAnimationClass = 'assignment-card-completing';

    const parentList = assignmentCard.parentElement;
    const cardHeight = assignmentCard.offsetHeight;

    // Lock current height so the transition has a start value
    assignmentCard.style.maxHeight = cardHeight + 'px';
    assignmentCard.style.overflow = 'hidden';
    void assignmentCard.offsetHeight; // reflow

    // Apply the CSS class (instant strikethrough + green flash)
    assignmentCard.classList.add('assignment-card-completing');

    // One-shot listener: fires when the collapse transition ends
    const onDone = (e) => {
      if (e.propertyName !== 'max-height') return; // only fire once on the longest prop
      assignmentCard.removeEventListener('transitionend', onDone);

      // Silently move to end of list and clean up
      assignmentCard.style.transition = 'none';
      if (parentList && assignmentCard.parentElement === parentList) {
        parentList.appendChild(assignmentCard);
      }
      assignmentCard.style.cssText = ''; // wipe inline styles
      assignmentCard.classList.remove('assignment-card-completing');

      window.scoutiqAnimatingAssignmentId = null;
      window.scoutiqAnimationClass = null;
      window.dispatchEvent(new CustomEvent('scoutiq:forceRefresh'));
    };
    assignmentCard.addEventListener('transitionend', onDone);

    // Kick off the single fluid transition — everything collapses together
    // Use rAF to guarantee the browser has painted the locked height first
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        assignmentCard.style.transition = 'max-height 0.32s cubic-bezier(.4,0,.2,1), opacity 0.24s ease-out, margin-bottom 0.32s cubic-bezier(.4,0,.2,1), padding 0.32s cubic-bezier(.4,0,.2,1)';
        assignmentCard.style.maxHeight = '0';
        assignmentCard.style.opacity = '0';
        assignmentCard.style.marginBottom = '0';
        assignmentCard.style.paddingTop = '0';
        assignmentCard.style.paddingBottom = '0';
      });
    });

    // Safety timeout in case transitionend never fires (e.g. element detached)
    setTimeout(() => {
      assignmentCard.removeEventListener('transitionend', onDone);
      if (window.scoutiqAnimatingAssignmentId) {
        assignmentCard.style.cssText = '';
        assignmentCard.classList.remove('assignment-card-completing');
        window.scoutiqAnimatingAssignmentId = null;
        window.scoutiqAnimationClass = null;
        window.dispatchEvent(new CustomEvent('scoutiq:forceRefresh'));
      }
    }, 600);

    console.log('[ScoutIQ Animation] Completion animation triggered');
  } catch (error) {
    console.error('Error triggering completion animation:', error);
  }
}

// Function to trigger smooth un-completion animation:
// Remove strikethrough + fade + collapse in one motion, then DOM moves to correct position.
function triggerUncompletingAnimation(assignmentCard) {
  try {
    window.scoutiqAnimatingAssignmentId = assignmentCard.dataset.assignmentId;
    window.scoutiqAnimationClass = 'assignment-card-uncompleting';

    const parentList = assignmentCard.parentElement;
    const cardHeight = assignmentCard.offsetHeight;

    assignmentCard.style.maxHeight = cardHeight + 'px';
    assignmentCard.style.overflow = 'hidden';
    void assignmentCard.offsetHeight; // reflow

    assignmentCard.classList.add('assignment-card-uncompleting');

    const onDone = (e) => {
      if (e.propertyName !== 'max-height') return;
      assignmentCard.removeEventListener('transitionend', onDone);

      assignmentCard.style.transition = 'none';
      if (parentList && assignmentCard.parentElement === parentList) {
        const firstCompleted = parentList.querySelector('.assignment-card--completed');
        if (firstCompleted && firstCompleted !== assignmentCard) {
          parentList.insertBefore(assignmentCard, firstCompleted);
        } else {
          parentList.insertBefore(assignmentCard, parentList.firstChild);
        }
      }
      assignmentCard.style.cssText = '';
      assignmentCard.classList.remove('assignment-card-uncompleting');

      window.scoutiqAnimatingAssignmentId = null;
      window.scoutiqAnimationClass = null;
      window.dispatchEvent(new CustomEvent('scoutiq:forceRefresh'));
    };
    assignmentCard.addEventListener('transitionend', onDone);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        assignmentCard.style.transition = 'max-height 0.32s cubic-bezier(.4,0,.2,1), opacity 0.24s ease-out, margin-bottom 0.32s cubic-bezier(.4,0,.2,1), padding 0.32s cubic-bezier(.4,0,.2,1)';
        assignmentCard.style.maxHeight = '0';
        assignmentCard.style.opacity = '0';
        assignmentCard.style.marginBottom = '0';
        assignmentCard.style.paddingTop = '0';
        assignmentCard.style.paddingBottom = '0';
      });
    });

    setTimeout(() => {
      assignmentCard.removeEventListener('transitionend', onDone);
      if (window.scoutiqAnimatingAssignmentId) {
        assignmentCard.style.cssText = '';
        assignmentCard.classList.remove('assignment-card-uncompleting');
        window.scoutiqAnimatingAssignmentId = null;
        window.scoutiqAnimationClass = null;
        window.dispatchEvent(new CustomEvent('scoutiq:forceRefresh'));
      }
    }, 600);

    console.log('[ScoutIQ Animation] Uncompleting animation triggered');
  } catch (error) {
    console.error('Error triggering uncompleting animation:', error);
  }
}

// Function to wire event card click handlers for modal
function wireEventCards(container) {
  if (!container) {
    console.warn('[ScoutIQ Wire] No container provided for events');
    return;
  }
  
  console.log('[ScoutIQ Wire] Starting to wire event cards in container:', container);
  console.log('[ScoutIQ Wire] Container innerHTML length:', container.innerHTML.length);
  console.log('[ScoutIQ Wire] Container children count:', container.children.length);
  
  // Find all event cards
  const eventCards = container.querySelectorAll('.event-card[data-event-id]');
  console.log('[ScoutIQ Wire] Query selector result:', eventCards);
  
  // Also try without the data attribute to see all event cards
  const allEventCards = container.querySelectorAll('.event-card');
  console.log('[ScoutIQ Wire] All event cards (no data-event-id filter):', allEventCards.length);
  
  if (allEventCards.length > 0) {
    console.log('[ScoutIQ Wire] Sample event card HTML:', allEventCards[0]?.outerHTML?.substring(0, 200));
    console.log('[ScoutIQ Wire] Sample event card dataset:', allEventCards[0]?.dataset);
  }
  
  if (!eventCards || eventCards.length === 0) {
    console.log('[ScoutIQ Wire] No event cards found in container');
    return;
  }
  console.log('[ScoutIQ Wire] Found', eventCards.length, 'event cards');
  
  eventCards.forEach((card, cardIndex) => {
    const id = card.dataset.eventId;
    
    if (!id) {
      return;
    }
    
    // Check if already bound
    if (card.dataset.cardClickBound === 'true') {
      return;
    }
        
    card.dataset.cardClickBound = 'true';
    
    // Add click handler to card to open modal
    card.addEventListener('click', (e) => {
      
      // Get the event object from the stored map
      const eventObject = window.scoutiqEventObjects?.get(String(id));
      if (eventObject) {
        window.dispatchEvent(new CustomEvent('scoutiq:openEventModal', { 
          detail: { event: eventObject } 
        }));
      } else {
        console.warn(`[ScoutIQ Card] No event object found for ID: ${id}`);
      }
    });
    
    // Add cursor pointer style to indicate clickability
    card.style.cursor = 'pointer';
    
  });
}

function wireAssignmentCheckboxes(container){
  if(!container) {
    console.warn('[ScoutIQ Wire] No container provided');
    return;
  }
  
  // Find all assignment cards
  const assignmentCards = container.querySelectorAll('.assignment-card[data-assignment-id]');
  if (!assignmentCards || assignmentCards.length === 0) {
    return;
  }
  
  assignmentCards.forEach((card, cardIndex) => {
    const id = card.dataset.assignmentId;
    const checkbox = card.querySelector('.assignment-card-checkbox input[type="checkbox"], .course-item-card-checkbox input[type="checkbox"]');
    const label = card.querySelector('.assignment-card-checkbox, .course-item-card-checkbox');
    
    if (!checkbox) {
      console.warn(`[ScoutIQ Wire] Card ${cardIndex}: No checkbox found`);
      return;
    }
    
    // Skip React-controlled checkboxes - check attribute multiple ways for compatibility
    const isReactControlled = checkbox.dataset.reactControlled === 'true' || 
                             checkbox.getAttribute('data-react-controlled') === 'true' ||
                             checkbox.hasAttribute('data-react-controlled');
    
    if (isReactControlled) {
      return;
    }
    
    if (!id) {
      console.warn(`[ScoutIQ Wire] Card ${cardIndex}: No assignment ID`);
      return;
    }
    
    // Check if already bound
    if (checkbox.dataset.bound === 'true') {
      return;
    }
    
    checkbox.dataset.bound = 'true';
    
    // Main change event handler
    const handleChange = async (e) => {
      console.log(`[ScoutIQ Checkbox] Change event fired for assignment ${id}`);
      
      // Prevent multiple simultaneous calls
      if (checkbox.dataset.processing === 'true') {
        console.log('[ScoutIQ Checkbox] Already processing, ignoring duplicate event');
        e.preventDefault();
        return;
      }
      
      const completed = checkbox.checked;
      
      console.log('[ScoutIQ Checkbox] State:', { 
        id, 
        completed
      });
      
      if (!id) {
        console.error('[ScoutIQ Checkbox] No assignment ID, reverting');
        checkbox.checked = !completed;
        alert('Error: Assignment ID not found. Cannot update.');
        return;
      }
      
      // Mark as processing (but don't disable - keep interactive)
      checkbox.dataset.processing = 'true';
      console.log('[ScoutIQ Checkbox] Processing assignment toggle...');
      
      // INSTANT UI UPDATE - Update card visual state immediately (optimistic)
      const raw = card.querySelector('.assignment-card-meta')?.textContent || '';
      card.classList.remove('assignment-card--completed','assignment-card--overdue','assignment-card--upcoming','assignment-card--default');
      
      if(completed){
        card.classList.add('assignment-card--completed');
        
        // Ensure strikethrough is visible immediately with concrete color
        const title = card.querySelector('.assignment-card-title');
        if (title) {
          // Use getComputedStyle to get the actual muted color value
          const mutedColor = getComputedStyle(document.documentElement).getPropertyValue('--semantic-text-muted').trim() || '#6B7280';
          title.style.textDecorationColor = mutedColor;
          title.style.color = mutedColor;
          console.log('[ScoutIQ Checkbox] Applied strikethrough with color:', mutedColor);
        }
        
        // Trigger completion animation (strikethrough + slide to bottom)
        triggerConfettiAnimation(card);
      } else {
        let isOverdue=false;
        try { 
          const dt = new Date(raw); 
          if(!isNaN(dt) && dt.getTime() < Date.now()) isOverdue=true; 
        } catch {}
        card.classList.add(isOverdue? 'assignment-card--overdue':'assignment-card--upcoming');
        // Trigger uncompleting animation (blue flash, strikethrough removal)
        triggerUncompletingAnimation(card);
      }
      
      try {
        console.log(`[ScoutIQ Checkbox] ${completed ? 'Completing' : 'Uncompleting'} assignment ${id}`);
        
        if(typeof window.scoutiqAssignmentToggle === 'function') {
          await window.scoutiqAssignmentToggle(id, completed);
          console.log(`[ScoutIQ Checkbox] ✅ Assignment ${id} API call successful`);
          
          // Clear processing flag immediately after successful API call
          delete checkbox.dataset.processing;
          
          // Notify React components
          try {
            const assignmentObject = window.scoutiqAssignmentObjects?.get(String(id));
            window.dispatchEvent(new CustomEvent('scoutiq:assignmentUpdated', { 
              detail: { 
                assignmentId: id, 
                completed,
                endeavorId: assignmentObject?.endeavorId || assignmentObject?.endeavorID || assignmentObject?.endeavor_id || null
              } 
            }));
            console.log('[ScoutIQ Checkbox] Event dispatched');
          } catch (e) {
            console.warn('[ScoutIQ Checkbox] Failed to dispatch event:', e);
          }
        } else {
          console.error('[ScoutIQ Checkbox] Toggle function not available');
          throw new Error('Assignment toggle function not available');
        }
      } catch(e) {
        console.error('[ScoutIQ Checkbox] ❌ Failed:', e);
        // Revert checkbox and card state on error
        checkbox.checked = !completed;
        
        // Revert card visual state
        card.classList.remove('assignment-card--completed','assignment-card--overdue','assignment-card--upcoming','assignment-card--default');
        if(!completed){
          card.classList.add('assignment-card--completed');
        } else {
          let isOverdue=false;
          try { 
            const dt = new Date(raw); 
            if(!isNaN(dt) && dt.getTime() < Date.now()) isOverdue=true; 
          } catch {}
          card.classList.add(isOverdue? 'assignment-card--overdue':'assignment-card--upcoming');
        }
        
        alert(`Failed to ${completed ? 'complete' : 'incomplete'} assignment: ${e.message}\n\nPlease try again.`);
      } finally {
        // Clear processing flag
        delete checkbox.dataset.processing;
        console.log('[ScoutIQ Checkbox] Processing complete');
      }
    };
    
    // Attach change listener to checkbox
    checkbox.addEventListener('change', handleChange);
    
    // Also attach click listener to label to ensure interaction is captured
    // (Label clicks should automatically trigger checkbox change, but we'll ensure it)
    if (label && label.dataset.clickBound !== 'true') {
      label.dataset.clickBound = 'true';
      label.addEventListener('click', (e) => {
        console.log(`[ScoutIQ Label] Label clicked for assignment ${id}`);
        // Don't prevent default - let the label naturally toggle the checkbox
        // which will trigger the change event
      });
    }
    
    // Add click handler to card body to open modal (except clicks on checkbox/label)
    if (card.dataset.cardClickBound !== 'true') {
      card.dataset.cardClickBound = 'true';
      card.addEventListener('click', (e) => {
        // Don't open modal if clicking on checkbox or its label
        if (e.target.closest('.assignment-card-checkbox')) {
          console.log(`[ScoutIQ Card] Click on checkbox area, ignoring for modal`);
          return;
        }
        
        console.log(`[ScoutIQ Card] Card clicked for assignment ${id}, dispatching modal event`);
        
        // Get the assignment object from the stored map
        const assignmentObject = window.scoutiqAssignmentObjects?.get(String(id));
        if (assignmentObject) {
          window.dispatchEvent(new CustomEvent('scoutiq:openAssignmentModal', { 
            detail: { assignment: assignmentObject } 
          }));
        } else {
          console.warn(`[ScoutIQ Card] No assignment object found for ID: ${id}`);
        }
      });
      
      // Add cursor pointer style to indicate clickability
      card.style.cursor = 'pointer';
    }
  });
}

if(typeof window !== 'undefined' && typeof window.scoutiqAssignmentToggle !== 'function'){
  window.scoutiqAssignmentToggle = async (id, completed) => {
    if(!id) {
      console.error('[ScoutIQ Toggle] No assignment ID provided');
      return;
    }
    
    // Set timestamp to prevent full re-render flash
    window.scoutiqLastToggleTime = Date.now();
    
    console.log('[ScoutIQ Toggle] Starting assignment toggle:', { id, completed });
    
    // Get the full assignment object that was stored during rendering
    const assignmentObject = window.scoutiqAssignmentObjects?.get(String(id));
    
    if (!assignmentObject) {
      console.error('[ScoutIQ Toggle] Could not find assignment object for ID:', id);
      console.log('[ScoutIQ Toggle] Available IDs:', window.scoutiqAssignmentObjects ? Array.from(window.scoutiqAssignmentObjects.keys()) : 'none');
      return;
    }
    
    console.log('[ScoutIQ Toggle] Found assignment object:', assignmentObject);
    
    // Use the completed parameter passed from the checkbox (the NEW state we want)
    const willBeCompleted = completed;
    
    console.log('[ScoutIQ Toggle] Assignment completion state:', {
      id,
      willBeCompleted,
      title: assignmentObject.title
    });
    
    // Optimistically update UI
    const card = document.querySelector(`.assignment-card[data-assignment-id="${id}"]`);
    
    // Calculate status for UI updates and payload
    const dueRaw = assignmentObject.dueAt || assignmentObject.due_at || assignmentObject.dueDate || assignmentObject.dueDatetime || assignmentObject.due;
    const now = new Date();
    const dueDate = dueRaw ? new Date(dueRaw) : null;
    const overdue = dueDate && !isNaN(dueDate) && dueDate < now;
    let nextStatus;
    if (willBeCompleted) {
      nextStatus = 'completed';
    } else {
      nextStatus = overdue ? 'overdue' : (dueRaw ? 'upcoming' : 'default');
    }
    
    if (card) {
      const checkbox = card.querySelector('input[type="checkbox"]');
      if (checkbox) {
        checkbox.checked = willBeCompleted;
      }
      // Update card status class
      card.className = card.className.replace(/assignment-card--\w+/, `assignment-card--${nextStatus}`);
    }
    
    try {
      // Use DataContext methods (same as Overview Dashboard)
      if (!window.dataContextInstance?.markAssignmentCompleted || !window.dataContextInstance?.updateAssignment) {
        throw new Error('DataContext methods not available');
      }
      
      if (willBeCompleted) {
        console.log('[ScoutIQ Toggle] Marking assignment as completed via DataContext...');
        const resp = await window.dataContextInstance.markAssignmentCompleted(id);
        console.log('[ScoutIQ Toggle] markAssignmentCompleted response:', {
          ok: resp?.ok,
          status: resp?.status,
          statusText: resp?.statusText
        });
        
        if (!resp?.ok) {
          const errorText = await resp.text().catch(() => 'Unknown error');
          throw new Error(`Failed to mark completed: ${resp.status} - ${errorText}`);
        }
        
        console.log('[ScoutIQ Toggle] ✅ Assignment marked as completed successfully');
      } else {
        console.log('[ScoutIQ Toggle] Marking assignment as incomplete via DataContext...');
        
        // Build comprehensive payload (same as Overview Dashboard)
        const payload = {
          assignmentId: id,
          title: assignmentObject.title || assignmentObject.name,
          description: assignmentObject.description || null,
          assignedDate: assignmentObject.assignedDate || null,
          dueDatetime: dueRaw || null,
          endeavorId: assignmentObject.endeavorId || assignmentObject.endeavorID || assignmentObject.endeavor_id || null,
          eventId: assignmentObject.eventId || assignmentObject.eventID || assignmentObject.event_id || null,
          priority: assignmentObject.priority || null,
          completed: false,
          status: nextStatus,
          completedDatetime: null
        };
        
        console.log('[ScoutIQ Toggle] Update payload:', payload);
        
        const resp = await window.dataContextInstance.updateAssignment(id, payload);
        console.log('[ScoutIQ Toggle] updateAssignment response:', {
          ok: resp?.ok,
          status: resp?.status,
          statusText: resp?.statusText
        });
        
        if (!resp?.ok) {
          const errorText = await resp.text().catch(() => 'Unknown error');
          throw new Error(`Failed to update assignment: ${resp.status} - ${errorText}`);
        }
        
        console.log('[ScoutIQ Toggle] ✅ Assignment marked as incomplete successfully');
      }
      
      // Update the stored assignment object
      assignmentObject.completed = willBeCompleted;
      assignmentObject.status = willBeCompleted ? 'Completed' : nextStatus;
      window.scoutiqAssignmentObjects.set(String(id), assignmentObject);
      
      // Update the effectiveAssignments array to reflect the change
      if (window.scoutiqEffectiveAssignments) {
        const assignmentIndex = window.scoutiqEffectiveAssignments.findIndex(a => {
          const aId = a.id || a.assignmentId || a.assignmentID || a.assignment_id;
          return String(aId) === String(id);
        });
        
        if (assignmentIndex !== -1) {
          console.log('[ScoutIQ Toggle] BEFORE update, assignment at index', assignmentIndex, ':', {
            completed: window.scoutiqEffectiveAssignments[assignmentIndex].completed,
            status: window.scoutiqEffectiveAssignments[assignmentIndex].status
          });
          
          window.scoutiqEffectiveAssignments[assignmentIndex] = {
            ...window.scoutiqEffectiveAssignments[assignmentIndex],
            completed: willBeCompleted,
            status: willBeCompleted ? 'Completed' : nextStatus
          };
          
          console.log('[ScoutIQ Toggle] AFTER update, assignment at index', assignmentIndex, ':', {
            completed: window.scoutiqEffectiveAssignments[assignmentIndex].completed,
            completedType: typeof window.scoutiqEffectiveAssignments[assignmentIndex].completed,
            status: window.scoutiqEffectiveAssignments[assignmentIndex].status,
            willBeCompleted,
            willBeCompletedType: typeof willBeCompleted
          });
          
          // Deduplicate the array to prevent duplicates from affecting calculations
          // CRITICAL: Keep FIRST occurrence to preserve just-toggled assignment state
          const uniqueMap = new Map();
          window.scoutiqEffectiveAssignments.forEach((a, idx) => {
            const aId = a.id || a.assignmentId || a.assignmentID || a.assignment_id;
            if (aId) {
              const key = String(aId);
              const existing = uniqueMap.get(key);
              
              // If this is the just-toggled assignment, log whether we're keeping or skipping
              if (key === String(id)) {
                console.log('[ScoutIQ Toggle] Deduplication step 1 - assignment', id, 'at index', idx, ':', {
                  completed: a.completed,
                  status: a.status,
                  alreadyHaveIt: !!existing,
                  existingCompleted: existing?.completed,
                  action: existing ? 'SKIP (keep first)' : 'KEEP'
                });
              }
              
              // Only set if we don't already have this ID (keep first occurrence)
              if (!existing) {
                uniqueMap.set(key, a);
              }
            }
          });
          window.scoutiqEffectiveAssignments = Array.from(uniqueMap.values());
          
          // Update progress bars directly without re-rendering all cards
          const currentAssignments = window.scoutiqEffectiveAssignments;
          
          // CRITICAL: Deduplicate before calculating to avoid counting duplicates
          const uniqueAssignments = new Map();
          currentAssignments.forEach(a => {
            const id = a.id || a.assignmentId || a.assignmentID || a.assignment_id;
            if (id) {
              uniqueAssignments.set(String(id), a);
            }
          });
          const deduplicatedAssignments = Array.from(uniqueAssignments.values());
          
          const totalAssignments = deduplicatedAssignments.length;
          const completedAssignments = deduplicatedAssignments.filter(a => {
            const isCompleted = a.completed === true || a.completed === 'true' || a.status === 'Completed' || a.submissionStatus === 'Completed';
            
            // Log the just-toggled assignment to see its state
            const aId = a.id || a.assignmentId || a.assignmentID || a.assignment_id;
            if (String(aId) === String(id)) {
              console.log('[ScoutIQ Toggle] Just-toggled assignment in filter:', {
                id: aId,
                completed: a.completed,
                completedType: typeof a.completed,
                status: a.status,
                submissionStatus: a.submissionStatus,
                isCompleted,
                willBeCompleted
              });
            }
            
            return isCompleted;
          }).length;
          const assignmentsPercent = totalAssignments > 0 ? Math.round((completedAssignments / totalAssignments) * 100) : 0;
          
          console.log('[ScoutIQ Toggle] Progress calculation:', {
            totalInArray: currentAssignments.length,
            totalUnique: totalAssignments,
            hasDuplicates: currentAssignments.length !== totalAssignments,
            completed: completedAssignments,
            percent: assignmentsPercent,
            justToggledTo: willBeCompleted ? 'completed' : 'incomplete',
            assignmentId: id
          });
          
          // Update progress bar (always update the visible legacy bar in course view)
          const assignmentsProgressFill = document.querySelector('[data-legacy-bar="true"]');
          const assignmentsCompletionText = document.querySelector('[data-legacy-text="true"]');
          if (assignmentsProgressFill) {
            assignmentsProgressFill.style.width = `${assignmentsPercent}%`;
            console.log('[ScoutIQ Toggle] Set progress bar width to:', assignmentsPercent + '%');
          }
          if (assignmentsCompletionText) {
            assignmentsCompletionText.textContent = `${completedAssignments} of ${totalAssignments} assignments completed (${assignmentsPercent}%)`;
            console.log('[ScoutIQ Toggle] Set progress text to:', assignmentsCompletionText.textContent);
          }
          
          // Update counts text
          const assignmentsCountsEl = document.querySelector('#course-assignments-counts');
          if (assignmentsCountsEl) {
            const remainingAssignments = totalAssignments - completedAssignments;
            assignmentsCountsEl.textContent = `${completedAssignments} completed, ${remainingAssignments} remaining`;
          }
          
          console.log('[ScoutIQ Toggle] ✅ Progress bars updated:', {
            total: totalAssignments,
            completed: completedAssignments,
            percent: assignmentsPercent
          });
        }
      }
      
      // Dispatch event for React components to update (same as Overview Dashboard)
      window.dispatchEvent(new CustomEvent('scoutiq:assignmentUpdated', { 
        detail: { 
          assignmentId: id, 
          completed: willBeCompleted,
          endeavorId: assignmentObject.endeavorId || assignmentObject.endeavorID || assignmentObject.endeavor_id || null
        }
      }));
      
    } catch (error) {
      console.error('[ScoutIQ Toggle] Error toggling assignment:', error);
      
      // Revert optimistic UI update on error (revert to opposite of what we tried)
      if (card) {
        const checkbox = card.querySelector('input[type="checkbox"]');
        if (checkbox) {
          checkbox.checked = !willBeCompleted; // Revert to opposite
        }
        const prevStatus = !willBeCompleted ? 'completed' : 'upcoming';
        card.className = card.className.replace(/assignment-card--\w+/, `assignment-card--${prevStatus}`);
      }
      
      throw error;
    }
  };
}
